import { Divider } from '@mui/material'
import { useFeatureFlagContext } from '@/feature_flags/FeatureFlagProvider'
import useOverlayLocalStorage from '@/hooks/useOverlayLocalStorage'
import { WorkflowState } from '@/types/workflows'
import CommentsDrawer from '@/components/comments/CommentsDrawer'
import useDocumentValidationSubscribe from '@/components/notifications/useDocumentValidationSubscribe'
import SplitDocumentView from '@/components/split-document/SplitDocumentView'
import ComparisonView from '@/components/validation/ComparisonView'
import { useDocumentChipsContext } from '@/components/validation/providers/DocumentChipsProvider'
import { useDocumentRowsContext } from '@/components/validation/providers/DocumentRowsProvider'
import { useDocumentRowValuesContext } from '@/components/validation/providers/DocumentRowValuesProvider'
import Validation from '@/components/validation/Validation'
import PurchaseOrderView from '../purchase-order-view/PurchaseOrderView'
import { ValidationOverlayContentContainerProps } from './ValidationOverlay'
import ValidationOverlayHeader from './ValidationOverlayHeader'

type ValidationOverlayContentProps = ValidationOverlayContentContainerProps & {
  documentView?: WorkflowState['default_document_view']
}

export default function ValidationOverlayContent({
  dataQueries,
  overlay,
  documentsCount,
  documentIsInFlight,
  documentView,
  hasPreviousDocument,
  hasNextDocument,
  selectedIndex,
  goToPreviousDocument,
  goToNextAvailableDocumentOrClose,
  refetchTableData,
  resetValue,
  setUseOnlySelected,
}: ValidationOverlayContentProps) {
  const documentQueryKey = dataQueries.documentQuery.queryKey
  const documentId = dataQueries.documentQuery.document?.id
  const { listQueryKey: chipsListQueryKey } = useDocumentChipsContext()
  const { queryKey: rowsListQueryKey } = useDocumentRowsContext()
  const { queryKey: rowValuesListQueryKey } = useDocumentRowValuesContext()

  // Comments panel shares the right-hand drawer slot with Field Details and is
  // mutually exclusive with it (see ValidationOverlayHeader).
  const commentsOverlay = useOverlayLocalStorage({
    localStorageKey: 'comments-drawer-is-open',
  })

  const featureFlags = useFeatureFlagContext()
  const showComments =
    documentView === 'validation' && !!featureFlags.document_comments

  useDocumentValidationSubscribe({
    documentId: dataQueries.documentQuery.document?.id,
    documentQueryKey,
    chipsListQueryKey,
    rowsListQueryKey,
    rowValuesListQueryKey,
  })

  return (
    <>
      <ValidationOverlayHeader
        documentView={documentView}
        documentQuery={dataQueries.documentQuery}
        commentsOverlay={commentsOverlay}
        onClose={() => {
          overlay.close()
          // clear doc param when closing overlay
          resetValue(false)
          refetchTableData()
          setUseOnlySelected(false)
        }}
        hasPreviousDocument={!documentIsInFlight && hasPreviousDocument}
        hasNextDocument={!documentIsInFlight && hasNextDocument}
        selectedIndex={selectedIndex}
        documentsCount={documentIsInFlight ? undefined : documentsCount}
        goToPreviousDocument={goToPreviousDocument}
        goToNextAvailableDocumentOrClose={goToNextAvailableDocumentOrClose}
      />
      <Divider />
      {showComments && (
        <CommentsDrawer overlay={commentsOverlay} documentId={documentId} />
      )}
      {documentView === 'validation' && (
        <Validation documentQuery={dataQueries.documentQuery} />
      )}
      {documentView === 'comparison' && (
        <ComparisonView
          documentQuery={dataQueries.documentQuery}
          key={dataQueries.documentQuery.document?.id} // This is to make it so it rerender when the document changes
        />
      )}
      {documentView === 'split-document' &&
        dataQueries.documentQuery.document && (
          <SplitDocumentView
            document={dataQueries.documentQuery.document}
            documentQueryKey={dataQueries.documentQuery.queryKey}
          />
        )}
      {documentView === 'purchase-order' &&
        dataQueries.documentQuery.document && (
          <PurchaseOrderView
            // Using document ID as key to force remount when document changes,
            // which resets the internal state of the PO view so we don't show
            // the previous document's PO data while loading the new document's PO data
            key={dataQueries.documentQuery.document.id}
            doc={dataQueries.documentQuery.document}
          />
        )}
    </>
  )
}
